'use client';

import InfoIcon from '@mui/icons-material/Info';
import {
	Button,
	Chip,
	CircularProgress,
	Unstable_Grid2 as Grid,
	MenuItem,
	Select,
	Switch,
	TextField,
	Tooltip,
} from '@mui/material';
import { gql, useLazyQuery, useMutation, useQuery } from '@parkchirp/api-client';
import {
	AutoInvoiceSettingEnum,
	AutoInvoiceSettingForPrismaEnum,
	RecurringPaymentsSettingDisplayEnum,
} from 'parkchirp-shared/enums';
import React, { FC, useCallback, useContext, useEffect, useState } from 'react';

import { CommonContext } from '../../contexts';
import {
	CREATE_SUBSCRIPTION_INVOICE_TEMPLATES,
	GET_ALL_INVOICE_TEMPLATES,
	GET_SUBSCRIPTION_INVOICE_TEMPLATES,
	GET_SUBSCRIPTION_SETTINGS,
	GET_USER_PAYMENT_METHODS,
	LIST_MANUAL_INVOICE_BILLING_SETTINGS,
	UPDATE_SUBSCRIPTION_SETTINGS,
} from '../../queries';
import { InvoiceTemplateSelection, UserPaymentMethods } from '../../types';
import { isOwnerPortal, isTenantPortal } from '../../utils';
import { InvoiceTemplateSelections } from '../invoices/invoiceTemplateSelections';

export const GET_FACILITY_SETTINGS = gql`
    query getFacilitySettings($facility_id: String!) {
        getFacilitySettings(facility_id: $facility_id) {
            use_ach
			use_credit_card
        }
    }
`;

const TRUE_UP_INVOICE_SETTINGS_FEATURE_FLAG = process.env.NEXT_PUBLIC_TRUE_UP_INVOICE_SETTINGS === 'true';
const BATCH_INVOICE_DAY_OPTIONS = Array.from({ length: 28 }, (_, idx) => idx + 1);

const getOrdinalSuffix = (day: number) => {
	if (day > 3 && day < 21) return `${day}th`;

	switch (day % 10) {
		case 1:
			return `${day}st`;
		case 2:
			return `${day}nd`;
		case 3:
			return `${day}rd`;
		default:
			return `${day}th`;
	}
};

type SubscriptionSettingsProps = {
	subscriptionId: string;
	facilityId: string;
	loading: boolean;
	setShowSnackbar: any;
	cognitoSub: string;
	paymentToken: string;
	refetch: any;
};

type ManualInvoiceBillingSetting = {
	id: number | null;
	profileName: string;
	billingDayInArrears: number;
	dueDayInArrears: number;
	billingDay: number;
	dueDay: number;
	facilityId?: string;
	hasError?: boolean;
};

export const SubscriptionSettings: FC<SubscriptionSettingsProps> = ({
	subscriptionId,
	facilityId,
	loading,
	cognitoSub,
	setShowSnackbar,
	paymentToken,
	refetch,
}) => {
	const [allowACHPayments, setAllowACHPayments] = useState<boolean>(true);
	const [pageReady, setPageReady] = useState<boolean>(false);
	const [editMode, setEditMode] = useState<boolean>(false);
	const [autoApplyCredits, setAutoApplyCredits] = useState<boolean>(true);
	const [allowCreditCardPayments, setAllowCreditCardPayments] = useState<boolean>(true);
	const [autoInvoiceSetting, setAutoInvoiceSetting] = useState<string>('INVOICE_NOW');
	const [batchNewParkers, setBatchNewParkers] = useState<boolean>(false);
	const [sendBatchInvoiceOn, setSendBatchInvoiceOn] = useState<number | ''>('');
	const [recurringPaymentsSetting, setRecurringPaymentsSetting] = useState('ALL_INVOICES');
	const [facilityAllowsACHPayments, setFacilityAllowsACHPayments] = useState<boolean>(true);
	const [facilityAllowsCreditCardPayments, setFacilityAllowsCreditCardPayments] = useState<boolean>(true);
	const [autoPay, setAutoPay] = useState<boolean>(false);
	const [creditCardFees, setCreditCardFees] = useState<boolean>(true);
	const [lateFees, setLateFees] = useState<boolean>(true);
	const [mailInvoiceFees, setMailInvoiceFees] = useState<boolean>(true);
	const [originalValues, setOriginalValues] = useState<any>({});
	const [transactionFees, setTransactionFees] = useState<boolean>(true);
	const [vehicleSurchargeFees, setVehicleSurchargeFees] = useState<boolean>(true);
	const [invoiceOption, setInvoiceOption] = useState<string>('Invoice Now');
	const [numberOfParkingSpaces, setNumberOfParkingSpaces] = useState<number | null>(null);
	const [tibaMType, setTibaMType] = useState<number | null>(null);
	const [poolSizeActive, setPoolSizeActive] = useState<boolean | null>(null);
	const [poolSizeLimit, setPoolSizeLimit] = useState<number | null>(null);
	const [integration, setIntegration] = useState<string | null>(null);
	const [unpaidInvoiceReminder, setUnpaidInvoiceReminder] = useState<boolean>(false);
	// Invoice Format Options
	const [monthlySelection, setMonthlySelection] = useState<InvoiceTemplateSelection>(null);
	const [carryoverSelection, setCarryoverSelection] = useState<InvoiceTemplateSelection>(null);
	const [validationSelection, setValidationSelection] = useState<InvoiceTemplateSelection>(null);
	const [hotelStorageSelection, setHotelStorageSelection] = useState<InvoiceTemplateSelection>(null);
	const [officeRetailSelection, setOfficeRetailSelection] = useState<InvoiceTemplateSelection>(null);
	const [miscSelection, setMiscSelection] = useState<InvoiceTemplateSelection>(null);
	const [invoiceFormatDataLoaded, setInvoiceFormatDataLoaded] = useState<boolean>(false);

	const [showACHPaymentTooltip, setShowACHPaymentTooltip] = useState<boolean>(false);
	const [showCreditCardPaymentTooltip, setShowCreditCardPaymentTooltip] = useState<boolean>(false);
	const { AccessControlledComponents, hasComponentAccess } = useContext(CommonContext);
	const canEditSubscriptionSettings =
		!AccessControlledComponents?.SubscriptionInfoSettingsEditButton ||
		hasComponentAccess(AccessControlledComponents.SubscriptionInfoSettingsEditButton);

	const { data: subscriptionInvoiceTemplateData, loading: loadingSubscriptionInvoiceTemplates } = useQuery(
		GET_SUBSCRIPTION_INVOICE_TEMPLATES,
		{
			variables: {
				subscriptionId,
			},
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
			},
			fetchPolicy: 'network-only',
			skip: !subscriptionId,
		},
	);

	const { data: allInvoiceTemplates, loading: loadingAllInvoiceTemplates } = useQuery(GET_ALL_INVOICE_TEMPLATES, {
		variables: {},
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'network-only',
	});

	useQuery(GET_FACILITY_SETTINGS, {
		variables: {
			facility_id: facilityId,
		},
		context: {
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'network-only',
		onCompleted: (data) => {
			if (data?.getFacilitySettings) {
				setFacilityAllowsACHPayments(data.getFacilitySettings.use_ach);
				setFacilityAllowsCreditCardPayments(data.getFacilitySettings.use_credit_card);
			}
		},
		skip: !facilityId,
	});

	const [createSubscriptionInvoiceTemplates, { loading: createSubscriptionInvoiceInProgress }] = useMutation(
		CREATE_SUBSCRIPTION_INVOICE_TEMPLATES,
		{
			context: {
				apiVersion: 'v2',
				appName: process.env.NEXT_PUBLIC_APP_NAME,
			},
		},
	);

	const [selectedBillingSetting, setSelectedBillingSetting] = useState<any>({
		id: null,
		profileName: 'Invoice Now',
		billingDayInArrears: 0,
		dueDayInArrears: 0,
		dueDay: 0,
		billingDay: 0,
	});
	const [loadedBillingSettings, setLoadedBillingSettings] = useState<boolean>(false);

	const [billingSettings, setBillingSettings] = useState<ManualInvoiceBillingSetting[]>([
		{
			id: null,
			profileName: 'Invoice Now',
			billingDayInArrears: 0,
			dueDayInArrears: 0,
			dueDay: 0,
			billingDay: 0,
		},
	]);

	const {
		data,
		error: queryError,
		loading: queryLoading,
	} = useQuery(GET_SUBSCRIPTION_SETTINGS, {
		variables: { subscriptionId },
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'network-only',
		skip: !subscriptionId,
	});

	const { data: facilityInvoiceBillingProfiles } = useQuery(LIST_MANUAL_INVOICE_BILLING_SETTINGS, {
		variables: { facilityId },
		context: {
			apiVersion: 'v2',
			appName: process.env.NEXT_PUBLIC_APP_NAME,
		},
		fetchPolicy: 'network-only',
		skip: !facilityId,
	});

	const [getUserPaymentMethods, { data: paymentMethodsData }] =
		useLazyQuery<UserPaymentMethods>(GET_USER_PAYMENT_METHODS);

	useEffect(() => {
		if (cognitoSub) {
			getUserPaymentMethods({
				variables: { userId: cognitoSub },
				context: {
					apiVersion: 'v2',
					appName: process.env.NEXT_PUBLIC_APP_NAME,
				},
				fetchPolicy: 'network-only',
			});
			refetch();
		}
	}, [cognitoSub, getUserPaymentMethods, refetch]);

	const isBankAccountSelectedForRecurringPayments: boolean =
		paymentMethodsData?.getUserPaymentMethods?.gpiBankAccounts?.some((account) => account.token === paymentToken);

	const [updateSubscriptionSettings, { loading: updateSubscriptionInProgress }] =
		useMutation(UPDATE_SUBSCRIPTION_SETTINGS);

	useEffect(() => {
		if (facilityInvoiceBillingProfiles && !loadedBillingSettings) {
			setBillingSettings([
				...billingSettings,
				...facilityInvoiceBillingProfiles.listManualInvoiceBillingSettings,
			]);
			setLoadedBillingSettings(true);
		}
	}, [billingSettings, facilityInvoiceBillingProfiles, loadedBillingSettings]);

	useEffect(() => {
		if (data && !queryError) {
			const subscription = data.getSubscriptionById;
			setAutoApplyCredits(subscription.autoApplyCredits);
			setAutoInvoiceSetting(subscription.autoInvoiceSetting);
			setBatchNewParkers(!!subscription.batchNewParkers);
			setSendBatchInvoiceOn(subscription.sendBatchInvoiceOn ?? '');
			setRecurringPaymentsSetting(subscription.recurringPaymentsSetting);
			setCreditCardFees(subscription.creditCardFees);
			setAutoPay(subscription.autoPay);
			setLateFees(subscription.lateFees);
			setMailInvoiceFees(subscription.mailInvoiceFees);
			setTransactionFees(subscription.transactionFees);
			setVehicleSurchargeFees(subscription.vehicleSurchargeFees);
			setNumberOfParkingSpaces(subscription.accessSystemMeta?.numberOfParkingSpaces);
			setTibaMType(subscription.accessSystemMeta?.tibaMType);
			setPoolSizeActive(subscription.accessSystemMeta?.poolSizeActive);
			setPoolSizeLimit(subscription.accessSystemMeta?.poolSizeLimit);
			setUnpaidInvoiceReminder(subscription.sendUnpaidInvoiceReminder);
			if (subscription.manualInvoiceBillingProfile) {
				setInvoiceOption(subscription.manualInvoiceBillingProfile.profileName);
				setSelectedBillingSetting(subscription.manualInvoiceBillingProfile);
			}
			setPageReady(true);
			setIntegration(subscription.billingProfile.facility.integration);
			setAllowACHPayments(!subscription.achDisabled);
			setAllowCreditCardPayments(subscription.allowCreditCardPayments);
		}
	}, [data, queryError]);

	const handleEdit = useCallback(() => {
		setOriginalValues({
			allowACHPayments,
			autoApplyCredits,
			autoInvoiceSetting,
			batchNewParkers,
			sendBatchInvoiceOn,
			creditCardFees,
			lateFees,
			mailInvoiceFees,
			transactionFees,
			numberOfParkingSpaces,
			poolSizeActive,
			poolSizeLimit,
			sendUnpaidInvoiceReminder: unpaidInvoiceReminder,
			allowCreditCardPayments,
			tibaMType,
		});
		setEditMode(true);
	}, [
		allowACHPayments,
		autoApplyCredits,
		autoInvoiceSetting,
		batchNewParkers,
		sendBatchInvoiceOn,
		creditCardFees,
		lateFees,
		mailInvoiceFees,
		transactionFees,
		numberOfParkingSpaces,
		poolSizeActive,
		poolSizeLimit,
		unpaidInvoiceReminder,
		allowCreditCardPayments,
		tibaMType,
	]);

	const handleSave = useCallback(async () => {
		const hasBatchInvoiceDay = sendBatchInvoiceOn !== '';

		if (batchNewParkers && !hasBatchInvoiceDay) {
			setShowSnackbar({
				message: 'Send True Up Invoice On is required when Batch New Parkers is enabled',
				severity: 'error',
			});
			return;
		}

		if (!batchNewParkers && hasBatchInvoiceDay) {
			setShowSnackbar({
				message: 'Batch New Parkers must be enabled when Send True Up Invoice On is selected',
				severity: 'error',
			});
			return;
		}

		if (batchNewParkers && autoInvoiceSetting !== 'AUTO_INVOICE') {
			setShowSnackbar({
				message: 'Batch New Parkers requires the Auto Invoice Setting to be set to Auto Invoice',
				severity: 'error',
			});
			return;
		}

		try {
			await updateSubscriptionSettings({
				variables: {
					facilityId,
					isFromTenantPortal: isTenantPortal,
					subscription: {
						autoApplyCredits,
						autoInvoiceSetting: AutoInvoiceSettingForPrismaEnum[autoInvoiceSetting],
						batchNewParkers,
						sendBatchInvoiceOn: hasBatchInvoiceDay ? Number(sendBatchInvoiceOn) : null,
						recurringPaymentsSetting,
						creditCardFees,
						lateFees,
						mailInvoiceFees,
						transactionFees,
						vehicleSurchargeFees,
						numberOfParkingSpaces,
						poolSizeActive,
						poolSizeLimit,
						sendUnpaidInvoiceReminder: unpaidInvoiceReminder,
						defaultInvoiceBillingProfileId: !selectedBillingSetting ? null : selectedBillingSetting.id,
						achDisabled: facilityAllowsACHPayments ? !allowACHPayments : true,
						allowCreditCardPayments,
						tibaMType,
					},
					subscriptionId,
				},
				context: {
					apiVersion: 'v2',
					appName: process.env.NEXT_PUBLIC_APP_NAME,
				},
			});
			const originalInvoiceTemplateIds = subscriptionInvoiceTemplateData.getSubscriptionInvoiceTemplates.map(
				(d) => d.id,
			);
			const invoiceTemplateIds = [];
			if (monthlySelection && !originalInvoiceTemplateIds.includes(monthlySelection.id)) {
				invoiceTemplateIds.push(monthlySelection.id);
			}
			if (carryoverSelection && !originalInvoiceTemplateIds.includes(carryoverSelection.id)) {
				invoiceTemplateIds.push(carryoverSelection.id);
			}
			if (validationSelection && !originalInvoiceTemplateIds.includes(validationSelection.id)) {
				invoiceTemplateIds.push(validationSelection.id);
			}
			if (hotelStorageSelection && !originalInvoiceTemplateIds.includes(hotelStorageSelection.id)) {
				invoiceTemplateIds.push(hotelStorageSelection.id);
			}
			if (officeRetailSelection && !originalInvoiceTemplateIds.includes(officeRetailSelection.id)) {
				invoiceTemplateIds.push(officeRetailSelection.id);
			}
			if (miscSelection && !originalInvoiceTemplateIds.includes(miscSelection.id)) {
				invoiceTemplateIds.push(miscSelection.id);
			}
			if (invoiceTemplateIds.length > 0) {
				await createSubscriptionInvoiceTemplates({
					variables: {
						subscriptionId,
						invoiceTemplateIds,
						facilityId,
					},
				});
			}
			setShowSnackbar({
				message: 'Subscription settings updated',
				severity: 'success',
			});
			setEditMode(false);
		} catch (err) {
			setShowSnackbar({
				message: err.message,
				severity: 'error',
			});
		}
	}, [
		updateSubscriptionSettings,
		facilityId,
		autoApplyCredits,
		autoInvoiceSetting,
		batchNewParkers,
		sendBatchInvoiceOn,
		recurringPaymentsSetting,
		creditCardFees,
		lateFees,
		mailInvoiceFees,
		transactionFees,
		numberOfParkingSpaces,
		unpaidInvoiceReminder,
		selectedBillingSetting,
		facilityAllowsACHPayments,
		allowACHPayments,
		subscriptionId,
		subscriptionInvoiceTemplateData?.getSubscriptionInvoiceTemplates,
		monthlySelection,
		carryoverSelection,
		validationSelection,
		hotelStorageSelection,
		officeRetailSelection,
		miscSelection,
		setShowSnackbar,
		createSubscriptionInvoiceTemplates,
		poolSizeActive,
		poolSizeLimit,
		allowCreditCardPayments,
		tibaMType,
		vehicleSurchargeFees,
	]);

	const handleChangeForInvoiceFormats = (e, setStateAction) => {
		const idToSearchFor = e.target.value;
		const templateToUse = allInvoiceTemplates?.getAllInvoiceTemplates.find((t) => t.id === idToSearchFor);
		setStateAction(templateToUse);
	};

	const setDefaultStates = useCallback(() => {
		setMiscSelection(
			subscriptionInvoiceTemplateData.getSubscriptionInvoiceTemplates.find(
				(oit) => oit.invoiceType === 'Miscellaneous',
			),
		);
		setMonthlySelection(
			subscriptionInvoiceTemplateData.getSubscriptionInvoiceTemplates.find(
				(oit) => oit.invoiceType === 'Monthly',
			),
		);
		setCarryoverSelection(
			subscriptionInvoiceTemplateData.getSubscriptionInvoiceTemplates.find(
				(oit) => oit.invoiceType === 'Carryover',
			),
		);
		setOfficeRetailSelection(
			subscriptionInvoiceTemplateData.getSubscriptionInvoiceTemplates.find(
				(oit) => oit.invoiceType === 'Office Retail',
			),
		);
		setHotelStorageSelection(
			subscriptionInvoiceTemplateData.getSubscriptionInvoiceTemplates.find(
				(oit) => oit.invoiceType === 'Hotel Storage',
			),
		);
		setValidationSelection(
			subscriptionInvoiceTemplateData.getSubscriptionInvoiceTemplates.find(
				(oit) => oit.invoiceType === 'Validation',
			),
		);
	}, [subscriptionInvoiceTemplateData?.getSubscriptionInvoiceTemplates]);

	const handleCancel = useCallback(() => {
		setAutoApplyCredits(originalValues.autoApplyCredits);
		setAutoInvoiceSetting(originalValues.autoInvoiceSetting);
		setBatchNewParkers(originalValues.batchNewParkers);
		setSendBatchInvoiceOn(originalValues.sendBatchInvoiceOn);
		setCreditCardFees(originalValues.creditCardFees);
		setLateFees(originalValues.lateFees);
		setMailInvoiceFees(originalValues.mailInvoiceFees);
		setTransactionFees(originalValues.transactionFees);
		setNumberOfParkingSpaces(originalValues.numberOfParkingSpaces);
		setTibaMType(originalValues.tibaMType);
		setUnpaidInvoiceReminder(originalValues.sendUnpaidInvoiceReminder);
		setAllowACHPayments(originalValues.allowACHPayments);
		setAllowCreditCardPayments(originalValues.allowCreditCardPayments);
		setDefaultStates();
		setEditMode(false);
	}, [originalValues, setDefaultStates]);

	useEffect(() => {
		if (
			!loadingSubscriptionInvoiceTemplates &&
			subscriptionInvoiceTemplateData &&
			!loadingAllInvoiceTemplates &&
			allInvoiceTemplates
		) {
			setDefaultStates();
			setInvoiceFormatDataLoaded(true);
		}
	}, [
		subscriptionInvoiceTemplateData,
		loadingSubscriptionInvoiceTemplates,
		invoiceFormatDataLoaded,
		loadingAllInvoiceTemplates,
		allInvoiceTemplates,
		setDefaultStates,
	]);

	const onTooltipOpen = useCallback(() => {
		if (editMode && !facilityAllowsCreditCardPayments) {
			setShowCreditCardPaymentTooltip(true);
		}
	}, [editMode, facilityAllowsCreditCardPayments, setShowCreditCardPaymentTooltip]);

	if (!pageReady) {
		if (queryError) console.log(queryError);

		return (
			<Grid container direction='column' alignItems='center' sx={{ marginTop: '75px' }}>
				{queryLoading ? <CircularProgress /> : <strong style={{ color: 'red' }}>Error loading page</strong>}
			</Grid>
		);
	}

	const billingSettingsOnChange = (e) => {
		const selected = billingSettings.find(({ profileName }) => profileName === e.target.value);
		setInvoiceOption(e.target.value);
		setSelectedBillingSetting(selected.billingDayInArrears === 0 ? null : selected);
	};

	const isBankAccountBeingUsedForRecurringPayments = autoPay && isBankAccountSelectedForRecurringPayments;

	return (
		<Grid container direction='column'>
			{!isOwnerPortal && (
				<Grid xsOffset={7} xs>
					{editMode && (
						<>
							<Button
								variant='contained'
								onClick={handleSave}
								sx={{ marginRight: '10px' }}
								data-cy='save-button'
							>
								{updateSubscriptionInProgress || createSubscriptionInvoiceInProgress ? (
									<CircularProgress size={25} sx={{ color: 'white' }} data-cy='loading-indicator' />
								) : (
									'Save'
								)}
							</Button>
							{!updateSubscriptionInProgress && !createSubscriptionInvoiceInProgress && (
								<Button
									sx={{ border: '1px solid' }}
									color='error'
									onClick={handleCancel}
									data-cy='cancel-button'
								>
									Cancel
								</Button>
							)}
						</>
					)}
					{!editMode && canEditSubscriptionSettings && (
						<Button disabled={loading} variant='contained' onClick={handleEdit} data-cy='edit-button'>
							Edit
						</Button>
					)}
				</Grid>
			)}
			<Grid container direction='row'>
				<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
					<strong>Auto Invoice Setting</strong>
				</Grid>
				{editMode ? (
					<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
						<Select
							value={autoInvoiceSetting}
							onChange={(e) => setAutoInvoiceSetting(e.target.value)}
							data-cy='auto-invoice-setting-select-menu'
						>
							{Object.keys(AutoInvoiceSettingEnum).map((key, i) => (
								<MenuItem value={key} key={i}>
									{AutoInvoiceSettingEnum[key]}
								</MenuItem>
							))}
						</Select>
					</Grid>
				) : (
					<span
						style={{ display: 'flex', justifyContent: 'left', alignItems: 'center' }}
						data-cy='auto-invoice-setting-text'
					>
						{AutoInvoiceSettingEnum[autoInvoiceSetting]}
					</span>
				)}
			</Grid>
			<Grid container direction='row'>
				<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
					<strong>Recurring Payments Setting</strong>
				</Grid>
				{editMode ? (
					<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
						<Select
							value={recurringPaymentsSetting}
							onChange={(e) => setRecurringPaymentsSetting(e.target.value)}
							data-cy='recurring-payments-setting-select-menu'
						>
							{Object.keys(RecurringPaymentsSettingDisplayEnum).map((key, i) => (
								<MenuItem value={key} key={i}>
									{RecurringPaymentsSettingDisplayEnum[key]}
								</MenuItem>
							))}
						</Select>
					</Grid>
				) : (
					<span
						style={{ display: 'flex', justifyContent: 'left', alignItems: 'center' }}
						data-cy='recurring-payments-setting-text'
					>
						{RecurringPaymentsSettingDisplayEnum[recurringPaymentsSetting]}
					</span>
				)}
			</Grid>
			{TRUE_UP_INVOICE_SETTINGS_FEATURE_FLAG && (
				<>
					<Grid container direction='row'>
						<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
							<strong>
								Batch New Parkers
								<Chip label='True Up' size='small' sx={{ marginLeft: 1, height: 20 }} />
								<Tooltip
									className='info-icon'
									title='When using True Up Invoice, the subscription must have the Auto Invoice Setting set to "Auto Invoice". This setting is located at the top of this page.'
									placement='right'
									arrow
									style={{ zIndex: 9999 }}
								>
									<InfoIcon fontSize='small' />
								</Tooltip>
							</strong>
						</Grid>
						<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
							<Switch
								checked={batchNewParkers}
								onChange={(e) => {
									const checked = e.target.checked;
									setBatchNewParkers(checked);
									if (!checked) {
										setSendBatchInvoiceOn('');
									}
								}}
								disabled={!editMode}
								data-cy='batch-new-parkers-switch'
							/>
						</Grid>
					</Grid>
					<Grid container direction='row'>
						<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
							<strong>
								Send True Up Invoice On
								<Chip label='True Up' size='small' sx={{ marginLeft: 1, height: 20 }} />
								<Tooltip
									className='info-icon'
									title='When using True Up Invoice, the subscription must have the Auto Invoice Setting set to "Auto Invoice". This setting is located at the top of this page.'
									placement='right'
									arrow
									style={{ zIndex: 9999 }}
								>
									<InfoIcon fontSize='small' />
								</Tooltip>
							</strong>
						</Grid>
						{editMode ? (
							<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
								<Select
									value={sendBatchInvoiceOn}
									onChange={(e) =>
										setSendBatchInvoiceOn(e.target.value === '' ? '' : Number(e.target.value))
									}
									displayEmpty
									disabled={!batchNewParkers}
									data-cy='send-batch-invoice-on-select-menu'
								>
									<MenuItem value=''>Select day</MenuItem>
									{BATCH_INVOICE_DAY_OPTIONS.map((day) => (
										<MenuItem key={day} value={day}>
											{day}
										</MenuItem>
									))}
								</Select>
							</Grid>
						) : (
							<span
								style={{ display: 'flex', justifyContent: 'left', alignItems: 'center' }}
								data-cy='send-batch-invoice-on-text'
							>
								{batchNewParkers && sendBatchInvoiceOn
									? `${getOrdinalSuffix(Number(sendBatchInvoiceOn))} day of the month`
									: '—'}
							</span>
						)}
					</Grid>
				</>
			)}
			<Grid container direction='row'>
				<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
					<strong>Auto-Apply Credits</strong>
				</Grid>
				<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
					<Switch
						checked={autoApplyCredits}
						onChange={(e) => setAutoApplyCredits(e.target.checked)}
						disabled={!editMode}
						data-cy='auto-apply-credits-switch'
					/>
				</Grid>
			</Grid>
			<Grid container direction='row'>
				<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
					<strong>Allow Credit Card Payments</strong>
				</Grid>
				<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
					<Tooltip
						title='Facility does not allow credit card payments'
						placement='right'
						open={showCreditCardPaymentTooltip}
						onOpen={onTooltipOpen}
						arrow
					>
						<Switch
							checked={allowCreditCardPayments}
							onChange={(e) => setAllowCreditCardPayments(e.target.checked)}
							disabled={!editMode || !facilityAllowsCreditCardPayments}
							data-cy='auto-apply-credits-switch'
						/>
					</Tooltip>
				</Grid>
			</Grid>
			<Grid container direction='row'>
				<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
					<strong>Allow ACH Payments</strong>
				</Grid>
				<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
					<Tooltip
						title={
							isBankAccountBeingUsedForRecurringPayments
								? `
                                ACH Payments cannot be disabled while
                                recurring payments are enabled with a bank account
                            `
								: 'Facility does not allow ACH Payments'
						}
						placement='right'
						open={showACHPaymentTooltip}
						arrow
					>
						<span
							onMouseEnter={() => {
								if (
									editMode &&
									(!facilityAllowsACHPayments || isBankAccountBeingUsedForRecurringPayments)
								) {
									setShowACHPaymentTooltip(true);
								}
							}}
							onMouseLeave={() => setShowACHPaymentTooltip(false)}
						>
							<Switch
								checked={allowACHPayments ?? true}
								onChange={(e) => setAllowACHPayments(e.target.checked)}
								disabled={
									!editMode ||
									!facilityAllowsACHPayments ||
									isBankAccountBeingUsedForRecurringPayments
								}
								data-cy='allow-ach-payments-switch'
							/>
						</span>
					</Tooltip>
				</Grid>
			</Grid>
			<Grid container direction='row'>
				<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
					<strong>Credit Card Fees</strong>
				</Grid>
				<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
					<Switch
						checked={creditCardFees}
						onChange={(e) => setCreditCardFees(e.target.checked)}
						disabled={!editMode}
						data-cy='credit-card-fees-switch'
					/>
				</Grid>
			</Grid>
			<Grid container direction='row'>
				<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
					<strong>Late Fees</strong>
				</Grid>
				<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
					<Switch
						checked={lateFees}
						onChange={(e) => setLateFees(e.target.checked)}
						disabled={!editMode}
						data-cy='late-fees-switch'
					/>
				</Grid>
			</Grid>
			<Grid container direction='row'>
				<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
					<strong>Mail Invoice Fees</strong>
				</Grid>
				<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
					<Switch
						checked={mailInvoiceFees}
						onChange={(e) => setMailInvoiceFees(e.target.checked)}
						disabled={!editMode}
						data-cy='mail-invoice-fees-switch'
					/>
				</Grid>
			</Grid>
			<Grid container direction='row'>
				<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
					<strong>Transaction Fees</strong>
				</Grid>
				<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
					<Switch
						checked={transactionFees}
						onChange={(e) => setTransactionFees(e.target.checked)}
						disabled={!editMode}
						data-cy='transaction-fees-switch'
					/>
				</Grid>
			</Grid>
			<Grid container direction='row'>
				<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
					<strong>Vehicle Surcharge Fees</strong>
				</Grid>
				<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
					<Switch
						checked={vehicleSurchargeFees}
						onChange={(e) => setVehicleSurchargeFees(e.target.checked)}
						disabled={!editMode}
						data-cy='vehicle-surcharge-fees-switch'
					/>
				</Grid>
			</Grid>
			<Grid container direction='row'>
				<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
					<strong>Send Unpaid Invoice Reminder</strong>
				</Grid>
				<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
					<Switch
						checked={unpaidInvoiceReminder}
						onChange={(e) => setUnpaidInvoiceReminder(e.target.checked)}
						disabled={!editMode}
						data-cy='unpaid-invoice-reminder-switch'
					/>
				</Grid>
			</Grid>
			<Grid container direction='row'>
				<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
					<strong>Manual Invoice Billing Setting</strong>
				</Grid>
				<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
					<Select value={invoiceOption} required disabled={!editMode} onChange={billingSettingsOnChange}>
						{billingSettings.map((type: any, index) => (
							<MenuItem disabled={type.profileName === '-'} key={index} value={type.profileName}>
								{type.profileName}
							</MenuItem>
						))}
					</Select>
				</Grid>
			</Grid>
			{integration === 'TIBA' && (
				<>
					<Grid container direction='row'>
						<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
							<strong>
								Integration Settings - Number of Parking Spaces <br /> (Tiba Only)
							</strong>
						</Grid>
						<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
							<TextField
								id='num-parking-spaces'
								name='num-parking-spaces'
								disabled={!editMode}
								value={numberOfParkingSpaces || null}
								onChange={(e) => setNumberOfParkingSpaces(Number(e.target.value))}
								type='number'
								inputProps={{ 'data-cy': 'number-of-parking-spaces-input' }}
							/>
						</Grid>
					</Grid>
					<Grid container direction='row'>
						<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
							<strong>
								Integration Settings - TIBA Active mType <br />
							</strong>
						</Grid>

						<Grid xs={3} display='flex' alignItems='center'>
							<TextField
								select
								fullWidth
								value={tibaMType ?? ''}
								label='Tiba Active mType'
								onChange={(e) => setTibaMType(Number(e.target.value))}
								disabled={!editMode}
							>
								<MenuItem value={2}>Standard</MenuItem>
								<MenuItem value={8}>Resider</MenuItem>
								<MenuItem value={4}>Master</MenuItem>
								<MenuItem value={1}>Units</MenuItem>
							</TextField>
						</Grid>
					</Grid>
				</>
			)}
			{integration === 'PARKONECT' && (
				<>
					<Grid container direction='row'>
						<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
							<strong>
								Integration Settings - Pool Size Active <br /> (Parkonect Only)
							</strong>
						</Grid>
						<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
							<Switch
								checked={poolSizeActive}
								onChange={(e) => setPoolSizeActive(e.target.checked)}
								disabled={!editMode}
								data-cy='pool-size-active-switch'
							/>
						</Grid>
					</Grid>
					<Grid container direction='row'>
						<Grid xs={3} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
							<strong>
								Integration Settings - Pool Size Limit
								<br /> (Parkonect Only)
							</strong>
						</Grid>
						<Grid xs={3} display='flex' justifyContent='left' alignItems='center'>
							<TextField
								id='pool-size-limit'
								name='pool-size-limit'
								disabled={!editMode}
								value={poolSizeLimit || null}
								onChange={(e) => setPoolSizeLimit(Number(e.target.value))}
								type='number'
								inputProps={{ 'data-cy': 'pool-size-limit-input' }}
							/>
						</Grid>
					</Grid>
				</>
			)}
			<Grid container direction='row'>
				<Grid
					style={{ zIndex: 9, marginTop: '-30px' }}
					xs={3}
					xsOffset={1}
					display='flex'
					justifyContent='left'
					alignItems='center'
				>
					<strong>
						Invoice Format Settings
						<Tooltip
							className='info-icon'
							title='Subscription invoice format settings have higher priority than both facility invoice format selection and organization invoice format selection.'
							placement='right'
							arrow
							style={{ zIndex: 9999 }}
						>
							<InfoIcon fontSize='small' />
						</Tooltip>
					</strong>
				</Grid>
			</Grid>
			<Grid container direction='row'>
				<Grid xs={6} xsOffset={1} display='flex' justifyContent='left' alignItems='center'>
					<InvoiceTemplateSelections
						allInvoiceTemplates={allInvoiceTemplates?.getAllInvoiceTemplates}
						handleChange={handleChangeForInvoiceFormats}
						monthlySelection={monthlySelection}
						carryoverSelection={carryoverSelection}
						validationSelection={validationSelection}
						hotelStorageSelection={hotelStorageSelection}
						officeRetailSelection={officeRetailSelection}
						miscSelection={miscSelection}
						setMonthlySelection={setMonthlySelection}
						setCarryoverSelection={setCarryoverSelection}
						setValidationSelection={setValidationSelection}
						setHotelStorageSelection={setHotelStorageSelection}
						setOfficeRetailSelection={setOfficeRetailSelection}
						setMiscSelection={setMiscSelection}
						editMode={editMode}
						dataLoaded={invoiceFormatDataLoaded}
					/>
				</Grid>
			</Grid>
		</Grid>
	);
};
